/**
 * Matter Assignment Editor Component
 * Admin UI for assigning/unassigning matters to a user
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';
import type { EchoMatter } from '../../api/types/echo';

export interface MatterAssignmentEditorProps {
  allMatters: EchoMatter[];
  assignedMatterIds: number[];
  onSave: (matterIds: number[]) => Promise<void>;
  isLoading?: boolean;
  isSaving?: boolean;
}

/**
 * Checklist UI for toggling matter assignments per user.
 * Displays all Echo matters with checkboxes indicating assignment status.
 */
export function MatterAssignmentEditor({
  allMatters,
  assignedMatterIds,
  onSave,
  isLoading = false,
  isSaving = false,
}: MatterAssignmentEditorProps) {
  const { theme } = useTheme();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(assignedMatterIds));
  const [hasChanges, setHasChanges] = useState(false);

  const mutedColor = theme.colors.text.secondary;
  const textColor = theme.colors.text.primary;

  // Sync local state when assigned IDs change from API
  useEffect(() => {
    setSelectedIds(new Set(assignedMatterIds));
    setHasChanges(false);
  }, [assignedMatterIds]);

  const handleToggle = useCallback((matterId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(matterId)) {
        next.delete(matterId);
      } else {
        next.add(matterId);
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    await onSave(Array.from(selectedIds));
    setHasChanges(false);
  }, [selectedIds, onSave]);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(allMatters.map((m) => m.id)));
    setHasChanges(true);
  }, [allMatters]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setHasChanges(true);
  }, []);

  if (isLoading) {
    return (
      <div style={{ padding: spacing.sm, color: mutedColor, fontSize: fontSize.sm }}>
        Loading matter assignments...
      </div>
    );
  }

  if (allMatters.length === 0) {
    return (
      <div style={{ padding: spacing.sm, color: mutedColor, fontSize: fontSize.sm }}>
        No matters available. Add matters in the Matters page first.
      </div>
    );
  }

  return (
    <Stack gap="sm">
      {/* Bulk actions */}
      <Flex justify="between" align="center">
        <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
          {selectedIds.size} of {allMatters.length} matters assigned
        </span>
        <Flex gap="sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            disabled={isSaving}
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeselectAll}
            disabled={isSaving}
          >
            Deselect All
          </Button>
        </Flex>
      </Flex>

      {/* Matter checklist */}
      <div
        style={{
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: '6px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        {allMatters.map((matter) => (
          <label
            key={matter.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              padding: `${spacing.xs} ${spacing.sm}`,
              borderBottom: `1px solid ${theme.colors.border.default}`,
              cursor: isSaving ? 'default' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
              fontSize: fontSize.sm,
            }}
          >
            <input
              type="checkbox"
              checked={selectedIds.has(matter.id)}
              onChange={() => handleToggle(matter.id)}
              disabled={isSaving}
              style={{ cursor: isSaving ? 'default' : 'pointer' }}
            />
            <span style={{ fontWeight: 600, color: textColor }}>{matter.mattercode}</span>
            <span style={{ color: mutedColor }}>{matter.name}</span>
          </label>
        ))}
      </div>

      {/* Save button */}
      {hasChanges && (
        <Flex justify="end">
          <Button
            variant="solid"
            color="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Assignments'}
          </Button>
        </Flex>
      )}
    </Stack>
  );
}

export default MatterAssignmentEditor;
